import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HubIcon from '@mui/icons-material/Hub';
import api from '../services/api';

const statusConfig = {
  approved: {
    icon: CheckCircleIcon,
    color: '#25D366',
    title: 'Pagamento Aprovado!',
    message: 'Seu canal foi ativado com sucesso. Voce ja pode comecar a usar.',
  },
  pending: {
    icon: HourglassEmptyIcon,
    color: '#F59E0B',
    title: 'Pagamento Pendente',
    message:
      'Seu pagamento esta sendo processado. Voce sera notificado quando o canal for ativado.',
  },
  failure: {
    icon: ErrorIcon,
    color: '#EF4444',
    title: 'Pagamento Nao Aprovado',
    message:
      'Houve um problema com o pagamento. Tente novamente ou escolha outra forma de pagamento.',
  },
};

export default function HubCheckoutReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);

  const status = searchParams.get('status') || 'pending';
  const sessionId =
    searchParams.get('session_id') ||
    searchParams.get('external_reference') ||
    sessionStorage.getItem('hub_checkout_session');

  const returnUrl = sessionStorage.getItem('hub_return_url');

  useEffect(() => {
    if (sessionId) {
      loadCheckout();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  // ⚠️ Notifica o admin do HUB (parent do iframe) que o checkout terminou.
  // Types aceitos: 'hub-plan-activated' (recarrega plano) e 'hub-marketplace-close'
  // (fecha iframe). Origin esperada pelo admin: https://hub.otimizapp.chat.
  // Ver contrato em ../PLAN.md seção 3 (postMessage).
  useEffect(() => {
    if (status !== 'approved') return;
    if (window.parent === window) return; // não estamos em iframe
    try {
      window.parent.postMessage(
        { type: 'hub-plan-activated', sessionId },
        'https://hub.otimizapp.chat',
      );
      // Fecha o iframe automaticamente após 3s
      const t = setTimeout(() => {
        window.parent.postMessage(
          { type: 'hub-marketplace-close' },
          'https://hub.otimizapp.chat',
        );
      }, 3000);
      return () => clearTimeout(t);
    } catch {
      // ignore — outra origem, ou parent bloqueou
    }
  }, [status, sessionId]);

  const loadCheckout = async () => {
    try {
      const { data } = await api.get(
        `/hub-subscriptions/checkout/${sessionId}`
      );
      setCheckout(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const StatusIcon = config.icon;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6, px: 4 }}>
          <StatusIcon
            sx={{ fontSize: 72, color: config.color, mb: 2 }}
          />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {config.title}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            {config.message}
          </Typography>

          {checkout && (
            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 2,
                p: 2,
                mb: 3,
                textAlign: 'left',
              }}
            >
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Canal:</strong> {checkout.plan?.name || checkout.channel}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Status:</strong> {checkout.status}
              </Typography>
              <Typography variant="body2">
                <strong>Sessao:</strong> {checkout.sessionId?.slice(0, 8)}...
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            {returnUrl ? (
              <Button
                variant="contained"
                startIcon={<HubIcon />}
                onClick={() => (window.location.href = returnUrl)}
                sx={{
                  bgcolor: '#FF6B00',
                  '&:hover': { bgcolor: '#E06000' },
                }}
              >
                Voltar ao Hub
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => navigate('/hub/subscriptions')}
                sx={{
                  bgcolor: '#FF6B00',
                  '&:hover': { bgcolor: '#E06000' },
                }}
              >
                Minhas Assinaturas
              </Button>
            )}

            {status === 'failure' && (
              <Button
                variant="outlined"
                onClick={() => navigate('/hub/marketplace')}
              >
                Tentar Novamente
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
